import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import InfluencersPage from "./pages/InfluencersPage";
import InfluencerDetailPage from "./pages/InfluencerDetailPage";
import ReelsPage from "./pages/ReelsPage";
import DelphiPage from "./pages/DelphiPage";
import BillingPage from "./pages/BillingPage";
import SettingsPage from "./pages/SettingsPage";
import DiagnosticsPage from "./pages/DiagnosticsPage";
import LibraryPage from "./pages/LibraryPage";
import AffiliatesPage from "./pages/AffiliatesPage";
import CreateStudioPage from "./pages/CreateStudioPage";
import RoadmapPage from "./pages/RoadmapPage";
import SchedulerPage from "./pages/SchedulerPage";
import EngagementInboxPage from "./pages/EngagementInboxPage";

// Studio Pages (Tab-based Creator Suite)
import ImagesStudioPage from "./pages/ImagesStudioPage";
import VideosStudioPage from "./pages/VideosStudioPage";
import MusicStudioPage from "./pages/MusicStudioPage";
import CombineStudioPage from "./pages/CombineStudioPage";
import TemplatesPage from "./pages/TemplatesPage";
import AppsPage from "./pages/AppsPage";
import CinemaStudioPage from "./pages/CinemaStudioPage";
import CanvasPage from "./pages/CanvasPage";
import OraclePage from "./pages/OraclePage";
import AutonomyCenterPage from "./pages/AutonomyCenterPage";
import AgentWorkforcePage from "./pages/AgentWorkforcePage";
import GrowthCommandCenterPage from "./pages/GrowthCommandCenterPage";
import LearningEnginePage from "./pages/LearningEnginePage";
import AutonomousDelphiPage from "./pages/AutonomousDelphiPage";
import RelightStudioPage from "./pages/RelightStudioPage";
import HelpPage from "./pages/HelpPage";
import ContactPage from "./pages/ContactPage";
import StatusPage from "./pages/StatusPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import AIContentPolicyPage from "./pages/AIContentPolicyPage";
import AdminDashboard from "./pages/AdminDashboard";
import TrialSetupPage from "./pages/TrialSetupPage";
import AffiliateKitPage from "./pages/AffiliateKitPage";
import CampaignPage from "./pages/CampaignPage";
import PreviewPage from "./pages/PreviewPage";
import SharePage from "./pages/SharePage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";

// Components
import OnboardingFlow from "./components/OnboardingFlow";
import OracleAssistant from "./components/OracleAssistant";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Protected Route with Onboarding Check + Trial Gate
const ProtectedRouteWithOnboarding = ({ children, allowTrialPending = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Check if user needs onboarding (not completed and not skipped)
  const needsOnboarding = !user.onboarding_completed && !user.onboarding_skipped;
  
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Trial gate: non-founders with trial_pending_payment go to trial setup
  if (!allowTrialPending && !user.is_founder && user.subscription_status === 'trial_pending_payment') {
    return <Navigate to="/trial-setup" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Authenticated Layout with Oracle Assistant
const AuthenticatedLayout = ({ children }) => {
  const { user } = useAuth();
  
  return (
    <>
      {children}
      {user && <OracleAssistant />}
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/preview/:creativeId" element={<PreviewPage />} />
      <Route path="/s/:shareId" element={<SharePage />} />
      <Route path="/auth" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      
      {/* Onboarding Route - Protected but doesn't redirect to itself */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingFlow />
        </ProtectedRoute>
      } />

      {/* Trial Setup Route */}
      <Route path="/trial-setup" element={
        <ProtectedRoute>
          <TrialSetupPage />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes with Onboarding Check */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <Dashboard />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/create" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <CreateStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/influencers" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <InfluencersPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/influencers/:id" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <InfluencerDetailPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/reels" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <ReelsPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/library" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <LibraryPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/delphi" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <DelphiPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/billing" element={
        <ProtectedRouteWithOnboarding allowTrialPending>
          <AuthenticatedLayout>
            <BillingPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/settings" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <SettingsPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/diagnostics" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <DiagnosticsPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/affiliates" element={
        <ProtectedRouteWithOnboarding allowTrialPending>
          <AuthenticatedLayout>
            <AffiliatesPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/affiliates/kit" element={<AffiliateKitPage />} />
      <Route path="/templates" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <TemplatesPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/apps" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <AppsPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/cinema" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <CinemaStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/canvas" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <CanvasPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/oracle" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <OraclePage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/autonomy" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <AutonomyCenterPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/workforce" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <AgentWorkforcePage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/growth-command" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <GrowthCommandCenterPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/learning" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <LearningEnginePage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/autonomous" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <AutonomousDelphiPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/relight" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <RelightStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      {/* Public support pages */}
      <Route path="/help" element={<HelpPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/status" element={<StatusPage />} />
      {/* Public legal pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/ai-content-policy" element={<AIContentPolicyPage />} />
      <Route path="/roadmap" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <RoadmapPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/scheduler" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <SchedulerPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/engagement" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <EngagementInboxPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      
      {/* Studio Routes - Tab-based Creator Suite */}
      <Route path="/campaigns" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <CampaignPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/campaign-analytics" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <CampaignAnalyticsPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/studio/combine" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <CombineStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/studio/images" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <ImagesStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/studio/videos" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <VideosStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      <Route path="/studio/music" element={
        <ProtectedRouteWithOnboarding>
          <AuthenticatedLayout>
            <MusicStudioPage />
          </AuthenticatedLayout>
        </ProtectedRouteWithOnboarding>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <div className="App dark">
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </FeatureFlagsProvider>
    </AuthProvider>
  );
}

export default App;

